import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import * as InfoCardConstants from '../../../components/lib/InfoCard/constants';
import * as D from '../deps';

export const pipedLinksMessages = defineMessages({
  family: {
    id: 'family',
    defaultMessage: 'family',
  },
  military: {
    id: 'military',
    defaultMessage: 'military',
  },
  student: {
    id: 'student',
    defaultMessage: 'student',
  },
});

export const UpgradeCTAMessages = defineMessages({
  durationForFree: {
    id: 'durationForFree',
    defaultMessage: '{trialDisplayableDuration} Free',
  },
  getDurationForFree: {
    id: 'getDurationForFree',
    defaultMessage: 'Get {trialDisplayableDuration} Free',
  },
  monthlyBilledLimited: {
    id: 'monthlyBilledLimited',
    defaultMessage: '{trialDisplayableDuration} for {displayablePrice} / {frequency}',
  },
  onceBilled: {
    id: 'onceBilled',
    defaultMessage: '{trialDisplayableDuration} for {displayablePrice}',
  },
  upgradeNow: {
    id: 'upgradeNow',
    defaultMessage: 'Upgrade Now',
  },
  signUpForFree: {
    id: 'signUpForFree',
    defaultMessage: 'Sign Up For Free',
  },
});

export const UpgradePlanMessages = defineMessages({
  personalizedStations: {
    id: 'personalizedStations',
    defaultMessage: 'Personalized stations',
  },
  adFreePersonalizedStations: {
    id: 'adFreePersonalizedStations',
    defaultMessage: 'Ad-free personalized stations',
  },
  adFreePersonalizedMusic: {
    id: 'adFreePersonalizedMusic',
    defaultMessage: 'Ad-free personalized music',
  },
  searchAndPlayWhatYouWant: {
    id: 'searchAndPlayWhatYouWant',
    defaultMessage: 'Search and play what you want',
  },
  enjoyYourFavoritePodcasts: {
    id: 'enjoyYourFavoritePodcasts',
    defaultMessage: 'Enjoy your favorite podcasts',
  },
  unlimitedSkips: {
    id: 'unlimitedSkips',
    defaultMessage: 'Unlimited skips',
  },
  offlineListening: {
    id: 'offlineListening',
    defaultMessage: 'Offline listening',
  },
  unlimitedOfflineListening: {
    id: 'unlimitedOfflineListening',
    defaultMessage: 'Unlimited offline listening',
  },
  makeAndSharePlaylists: {
    id: 'makeAndSharePlaylists',
    defaultMessage: 'Make and share playlists',
  },
  discountedPremiumPlans: {
    id: 'discountedPremiumPlans',
    defaultMessage: 'Discounted Premium Plans',
  },
  messageWithAsterisk: {
    id: 'messageWithAsterisk',
    defaultMessage: '{message} *',
  },
  unlockByViewingAnAd: {
    id: 'unlockByViewingAnAd',
    defaultMessage: '* Unlock by viewing an ad',
  },
});

export const FamilyPlanMessages = defineMessages({
  upToSixAccounts: {
    id: 'upToSixAccounts',
    defaultMessage: 'Up to six accounts',
  },
  easyBill: {
    id: 'easyBill',
    defaultMessage: 'One low price, one easy bill',
  },
  newOrExistingListeners: {
    id: 'newOrExistingListeners',
    defaultMessage: 'New or existing listeners',
  },
  keepYourMusic: {
    id: 'keepYourMusic',
    defaultMessage: 'Keep your own music',
  },
  allPandoraPremiumFeatures: {
    id: 'allPandoraPremiumFeatures',
    defaultMessage: 'All Pandora {premiumFeaturesLink}',
  },
  premiumFeatures: {
    id: 'premiumFeatures',
    defaultMessage: 'Premium Features',
  },
});

export const PandoraPremiumFeaturesList = [
  <FormattedMessage key={0} {...UpgradePlanMessages.adFreePersonalizedMusic} />,
  <FormattedMessage key={1} {...UpgradePlanMessages.searchAndPlayWhatYouWant} />,
  <FormattedMessage key={2} {...UpgradePlanMessages.enjoyYourFavoritePodcasts} />,
  <FormattedMessage key={3} {...UpgradePlanMessages.unlimitedSkips} />,
  <FormattedMessage key={4} {...UpgradePlanMessages.unlimitedOfflineListening} />,
  <FormattedMessage key={5} {...UpgradePlanMessages.makeAndSharePlaylists} />,
] as const;

export const UpgradeCardData = {
  AD_SUPPORTED: {
    NONE: {
      offerType: 'Pandora',
      ctaButtonHref: '/upgrade',
      infoContentActiveList: [
        <FormattedMessage key={0} {...UpgradePlanMessages.personalizedStations} />,
        <FormattedMessage
          key={1}
          {...UpgradePlanMessages.messageWithAsterisk}
          values={{
            message: <FormattedMessage {...UpgradePlanMessages.searchAndPlayWhatYouWant} />,
          }}
        />,
        <FormattedMessage key={2} {...UpgradePlanMessages.enjoyYourFavoritePodcasts} />,
        <FormattedMessage
          key={3}
          {...UpgradePlanMessages.messageWithAsterisk}
          values={{
            message: <FormattedMessage {...UpgradePlanMessages.unlimitedSkips} />,
          }}
        />,
      ],
      infoContentInactiveList: [
        <FormattedMessage key={0} {...UpgradePlanMessages.offlineListening} />,
        <FormattedMessage key={1} {...UpgradePlanMessages.makeAndSharePlaylists} />,
      ],
    },
  },
  PANDORA_PLUS: {
    NONE: {
      offerType: 'Pandora Plus',
      ctaButtonHref: D.Utils.Route.getPlusPaymentPath(),
      theme: InfoCardConstants.InfoCardTheme.PANDORA_PLUS,
      infoContentActiveList: [
        <FormattedMessage key={0} {...UpgradePlanMessages.adFreePersonalizedStations} />,
        <FormattedMessage
          key={1}
          {...UpgradePlanMessages.messageWithAsterisk}
          values={{
            message: <FormattedMessage {...UpgradePlanMessages.searchAndPlayWhatYouWant} />,
          }}
        />,
        <FormattedMessage key={2} {...UpgradePlanMessages.enjoyYourFavoritePodcasts} />,
        <FormattedMessage key={3} {...UpgradePlanMessages.unlimitedSkips} />,
        <FormattedMessage key={4} {...UpgradePlanMessages.offlineListening} />,
      ],
      infoContentInactiveList: [
        <FormattedMessage key={0} {...UpgradePlanMessages.makeAndSharePlaylists} />,
      ],
    },
  },
  PANDORA_PREMIUM: {
    NONE: {
      offerType: 'Pandora Premium',
      ctaButtonHref: D.Utils.Route.getPremiumPaymentPath(),
      theme: InfoCardConstants.InfoCardTheme.PANDORA_PREMIUM,
      infoContentActiveList: PandoraPremiumFeaturesList,
    },
    MILITARY: {
      offerType: 'Military',
      ctaButtonHref: D.Utils.Route.getPremiumMilitaryPlanPaymentPath(),
      theme: InfoCardConstants.InfoCardTheme.PANDORA_PREMIUM,
      infoContentActiveList: PandoraPremiumFeaturesList,
    },
    STUDENT: {
      offerType: 'Student',
      ctaButtonHref: D.Utils.Route.getPremiumStudentPlanPaymentPath(),
      theme: InfoCardConstants.InfoCardTheme.PANDORA_PREMIUM,
      infoContentActiveList: PandoraPremiumFeaturesList,
    },
  },
  PREMIUM_FAMILY_PLAN: {
    NONE: {
      offerType: 'Family',
      ctaButtonHref: D.Utils.Route.getFamilyPlanPaymentPath(),
      theme: InfoCardConstants.InfoCardTheme.PANDORA_PREMIUM,
      infoContentActiveList: [
        <FormattedMessage key={0} {...FamilyPlanMessages.upToSixAccounts} />,
        <FormattedMessage key={1} {...FamilyPlanMessages.easyBill} />,
        <FormattedMessage key={2} {...FamilyPlanMessages.newOrExistingListeners} />,
        <FormattedMessage key={3} {...FamilyPlanMessages.keepYourMusic} />,
      ],
    },
  },
};
